import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import Resumable from 'resumablejs';
import CheckIcon from '@mui/icons-material/Check';

const FileUpload = () => {
  // Grouped state: key = file's uniqueIdentifier, value = upload info
  const [uploadState, setUploadState] = useState({});
  const [outputFormat, setOutputFormat] = useState('mp4');

  // For sequential conversion.
  const [conversionQueue, setConversionQueue] = useState([]);
  const [activeConversion, setActiveConversion] = useState(null);

  const resumableRef = useRef(null);
  const startTimeRef = useRef({});
  const pollIntervalRef = useRef({});
  const allowedExtensions = ['mp4', 'avi', 'mov', 'mp3', 'wav', 'aac'];
  const token = localStorage.getItem('token');
  const currentUserPlan = 'guest';
  const MAX_FILE_SIZES = {
    guest: 50 * 1024 * 1024,
    basic: 500 * 1024 * 1024,
    advanced: 1024 * 1024 * 1024,
  };

  // Initialize Resumable.
  const initResumable = () => {
    if (!resumableRef.current) {
      const r = new Resumable({
        target: '/app/upload',
        chunkSize: 1 * 1024 * 1024,
        simultaneousUploads: 2,
        testChunks: false,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Authorization: `Bearer ${token}`,
          'X-Guest-ID': localStorage.getItem('guestId'), // Include guest ID in headers
        },
        generateUniqueIdentifier: (file) => {
          const timestamp = Math.floor(Date.now() / 1000);
          return file.name ? `${timestamp}_${file.size}-${file.name.replace(/[^a-zA-Z0-9]/g, '')}` : false;
        },
      });

      // When a file is added, initialize its state.
      r.on('fileAdded', (file) => {
        const identifier = file.uniqueIdentifier;
        // Use file.name for display.
        const displayName = file.fileName;
		console.log('file name: ',displayName);
        setUploadState(prevState => ({
          ...prevState,
          [identifier]: {
            fileName: displayName,
            fileSize: file.size,
            status: 'Waiting in queue',
            readyForAssembly: false,
            totalChunks: 0,
            uploadedChunks: 0,
            progress: 0,
            speed: 0,
            remainingTime: 0,
            retries: 0,
            failedChunks: [],
            metadata: null,
            convertedFileUrl: null,
            polling: false,
          }
        }));
        // Use displayName for start time
        startTimeRef.current[displayName] = Date.now();
        setTimeout(() => r.upload(), 0);
      });

      // When file progress is reported, update state.
      r.on('fileProgress', async (file) => {
        const identifier = file.uniqueIdentifier;
        // Always reassign fileName from the previous state or file.name if not available.
        const displayName = (uploadState[identifier] && uploadState[identifier].fileName) || file.name;
        const uploadedChunks = file.chunks.filter(chunk => chunk.status() === 'success').length;
        const totalChunks = file.chunks.length;
        const elapsed = (Date.now() - startTimeRef.current[displayName]) / 1000;
        const speed = (file.size * file.progress()) / elapsed;
        const remainingChunks = totalChunks - uploadedChunks;
        const estTime = remainingChunks * (elapsed / uploadedChunks) || 0;
        
        setUploadState(prevState => ({
          ...prevState,
          [identifier]: {
            ...prevState[identifier],
            // Ensure fileName is preserved.
            fileName: prevState[identifier]?.fileName || displayName,
            uploadedChunks,
            totalChunks,
            progress: Math.floor(file.progress() * 100),
            speed: (speed / 1024).toFixed(2),
            remainingTime: Math.floor(estTime),
            // Update status if it is still waiting.
            status: prevState[identifier]?.status === 'Waiting in queue'
              ? 'Uploading...'
              : prevState[identifier].status,
          }
        }));
  
        // Only trigger conversion if totalChunks > 0 and upload is complete.
        if (totalChunks > 0 && uploadedChunks === totalChunks) {
          console.log('All chunks uploaded for', displayName);
          setUploadState(prevState => ({
            ...prevState,
            [identifier]: {
              ...prevState[identifier],
              status: 'Upload complete. Waiting for conversion...',
              readyForAssembly: true,
              progress: 100,
            }
          }));
          setConversionQueue(prev => {
            if (!prev.includes(identifier)) return [...prev, identifier];
            return prev;
          });
        }
      });
  
      r.on('fileError', (file, message) => {
        const identifier = file.uniqueIdentifier;
        setUploadState(prevState => ({
          ...prevState,
          [identifier]: {
            ...prevState[identifier],
            status: `Error: ${message}`,
          }
        }));
      });
  
      resumableRef.current = r;
    }
  };

  // Process conversions one at a time.
  useEffect(() => {
    if (!activeConversion && conversionQueue.length > 0) {
      const nextId = conversionQueue[0];
      setActiveConversion(nextId);
      (async () => {
        console.log('Starting conversion for', nextId);
        await initiateAssembly(nextId, uploadState[nextId].totalChunks);
        startPolling(nextId);
      })();
    }
  }, [activeConversion, conversionQueue, uploadState]);
  
  // Once a conversion completes, remove it from the queue.
  useEffect(() => {
    if (activeConversion && uploadState[activeConversion]?.status === 'Conversion complete') {
      setConversionQueue(prev => prev.filter(id => id !== activeConversion));
      setActiveConversion(null);
    }
  }, [uploadState, activeConversion]);
  
  // Call the /app/assemble endpoint.
  const initiateAssembly = async (identifier, totalChunks) => {
    try {
      const guestId = localStorage.getItem('guestId');
      const response = await fetch('/app/assemble', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          'X-Guest-ID': guestId, // Include guest ID
        },
        body: JSON.stringify({
          resumableIdentifier: identifier,
          totalChunks,
          outputFormat,
          guestId, // Explicitly pass guest ID
        }),
      });
      const result = await response.json();
      console.log('Assembly started for', identifier, result);
      setUploadState(prevState => ({
        ...prevState,
        [identifier]: {
          ...prevState[identifier],
          status: 'Assembling File and Converting',
        }
      }));
    } catch (error) {
      console.error(`Error assembling file ${identifier}:`, error);
      setUploadState(prevState => ({
        ...prevState,
        [identifier]: {
          ...prevState[identifier],
          status: 'Assembly failed',
        }
      }));
    }
  };
  
  // Poll conversion progress.
  const startPolling = (identifier) => {
    if (pollIntervalRef.current[identifier]) return;
    setUploadState(prevState => ({
      ...prevState,
      [identifier]: {
        ...prevState[identifier],
        polling: true,
        progress: 0,
      }
    }));
    pollIntervalRef.current[identifier] = setInterval(async () => {
      try {
        const response = await fetch(`/app/track_progress/${identifier}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json();
        let metadata = data.metadata;
        if (metadata && typeof metadata === 'string') {
          try {
            metadata = JSON.parse(metadata);
          } catch (e) {
            console.error('Error parsing metadata:', e);
            metadata = null;
          }
        }
        if (metadata && Object.keys(metadata).length > 0) {
          setUploadState(prevState => ({
            ...prevState,
            [identifier]: {
              ...prevState[identifier],
              status: 'Conversion complete',
              polling: false,
              convertedFileUrl: data.convertedFileUrl,
              metadata,
              progress: 100,
            }
          }));
          clearInterval(pollIntervalRef.current[identifier]);
        } else {
          setUploadState(prevState => ({
            ...prevState,
            [identifier]: {
              ...prevState[identifier],
              progress: data.percentage,
            }
          }));
        }
      } catch (err) {
        setUploadState(prevState => ({
          ...prevState,
          [identifier]: {
            ...prevState[identifier],
            status: 'Error tracking progress',
            polling: false,
          }
        }));
        clearInterval(pollIntervalRef.current[identifier]);
      }
    }, 2000);
  };
  
  const retryFailedChunks = (identifier) => {
    setUploadState(prevState => ({
      ...prevState,
      [identifier]: {
        ...prevState[identifier],
        retries: prevState[identifier].retries + 1,
      }
    }));
    const file = findFileByIdentifier(identifier);
    (uploadState[identifier].failedChunks || []).forEach((failedChunk) => {
      const chunk = file?.chunks[failedChunk.chunkNumber - 1];
      if (chunk) {
        chunk.preprocessState = 0;
        chunk.start();
      }
    });
    setUploadState(prevState => ({
      ...prevState,
      [identifier]: {
        ...prevState[identifier],
        failedChunks: [],
      }
    }));
  };
  
  const cancelUpload = (identifier) => {
    const file = findFileByIdentifier(identifier);
    if (file) file.cancel();
    setUploadState(prevState => ({
      ...prevState,
      [identifier]: {
        ...prevState[identifier],
        status: 'Upload canceled',
      }
    }));
  };
  
  const findFileByIdentifier = (identifier) => {
    const resumable = resumableRef.current;
    return resumable.files.find(f => f.uniqueIdentifier === identifier);
  };
  
  // Handle file drop.
  const onDrop = useCallback((acceptedFiles) => {
    const newFiles = acceptedFiles.filter(file =>
      allowedExtensions.includes(file.name.split('.').pop().toLowerCase())
    );
    if (newFiles.length > 0 && resumableRef.current) {
      resumableRef.current.addFiles(newFiles);
    }
    // File state is initialized in fileAdded.
  }, []);
  
  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: allowedExtensions.map(ext => `.${ext}`).join(','),
  });
  
  // Overall progress summary.
  const totalFileSize = Object.values(uploadState).reduce((acc, data) => acc + (data.fileSize || 0), 0);
  const totalUploadedBytes = Object.values(uploadState).reduce((acc, data) =>
    acc + ((data.progress / 100) * (data.fileSize || 0)), 0);
  const remainingBytes = totalFileSize - totalUploadedBytes;
  const bytesToMB = (bytes) => (bytes / (1024 * 1024)).toFixed(2);
  const totalSizeMB = bytesToMB(totalFileSize);
  const uploadedMB = bytesToMB(totalUploadedBytes);
  const remainingMB = bytesToMB(remainingBytes);
  
  // Overall conversion summary.
  const totalFiles = Object.keys(uploadState).length;
  const completeCount = Object.values(uploadState).filter(
    (data) => data.status === 'Conversion complete'
  ).length;
  
  useEffect(() => {
    initResumable();
  }, []);
console.warn('uploadState',uploadState);
  return (
    <div className="flex flex-col items-center">
      {/* Sticky Header */}
      <div className="sticky top-0 z-50 bg-white w-full px-4 py-2 border-b">
        <h2 className="text-xl font-bold mb-2 text-center">Upload & Convert</h2>
        {totalFiles > 0 && (
          <div className="mb-2 text-sm text-gray-700 text-center">
            {completeCount}/{totalFiles} conversions complete&nbsp;|&nbsp;
            Uploaded: {uploadedMB} MB / {totalSizeMB} MB&nbsp;|&nbsp;
            Remaining: {remainingMB} MB
          </div>
        )}
        <div
          {...getRootProps({
            className: 'border-2 border-dashed p-4 text-center cursor-pointer',
          })}
        >
          <input {...getInputProps()} accept="video/*" />
          <p className="text-gray-500">Drag & drop files here, or click to select</p>
        </div>
        <div className="mt-2">
          <label className="block text-sm font-medium text-gray-700">Output Format:</label>
          <select
            value={outputFormat}
            onChange={(e) => setOutputFormat(e.target.value)}
            className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
          >
            {allowedExtensions.map((ext) => (
              <option key={ext} value={ext}>
                {ext.toUpperCase()}
              </option>
            ))}
          </select>
        </div>
      </div>
  
      {/* Main Content */}
      <div className="bg-white p-6 rounded-lg shadow-lg w-96 mt-20">
        <div className="mt-4 space-y-4">
          {Object.entries(uploadState).map(([id, data]) => (
            <div key={id} className="p-4 border rounded-lg shadow">
              {/* File Name & Status */}
              <div className="flex justify-between items-center mb-2">
                <span className="text-lg font-bold">{data.fileName || id}</span>
                {data.convertedFileUrl && <CheckIcon className="text-green-500 w-5 h-5" />}
              </div>
  
              {data.status && (
                <p className="text-xs text-gray-600 mb-1">{data.status}</p>
              )}
  
              {/* Upload Progress */}
              {data.progress > 0 && (
                <>
                  <progress
                    value={data.progress}
                    max="100"
                    className="w-full h-2 rounded"
                  />
                  <div className="text-xs text-gray-600 mt-1">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <div className="font-medium">Chunks</div>
                        <div>{data.uploadedChunks || 0}/{data.totalChunks || 0}</div>
                      </div>
                      <div>
                        <div className="font-medium">Speed</div>
                        <div>{data.speed || '0.0'} KB/s</div>
                      </div>
                      <div>
                        <div className="font-medium">ETA</div>
                        <div>{data.remainingTime || 0}s</div>
                      </div>
                    </div>
                  </div>
                </>
              )}
  
              {/* Thumbnail & Metadata */}
              {data.metadata && (
                <div className="flex items-center mt-2 border-t pt-2">
                  <img
                    className="w-20 rounded-lg shadow mr-3"
                    src={`/app/uploads/${data.metadata.thumbnailPath}`}
                    alt="Thumbnail"
                  />
                  <div>
                    <p className="text-xs text-gray-700">
                      Duration: {data.metadata.duration ? data.metadata.duration.toFixed(1) + 's' : 'N/A'}
                    </p>
                    <p className="text-xs text-gray-500">
                      Uploaded: {new Date(data.metadata.uploadDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              )}
  
              {/* Error Messages */}
              {data.error && (
                <p className="text-xs text-red-500 mt-1">{data.error}</p>
              )}
  
              {/* Retry Conversion Button */}
              {data.status === 'Error tracking progress' && (
                <button
                  onClick={() => startPolling(id)}
                  className="mt-2 px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  Retry Conversion
                </button>
              )}
  
              {/* Retry Failed Chunks */}
              {data.failedChunks && data.failedChunks.length > 0 && (
                <button
                  onClick={() => retryFailedChunks(id)}
                  className="mt-2 px-2 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600"
                >
                  Retry Failed Chunks ({data.failedChunks.length})
                </button>
              )}
  
              {/* Assembly Errors */}
              {data.assemblyError && (
                <p className="text-xs text-red-500 mt-1">{data.assemblyError}</p>
              )}
  
              {/* Download Button */}
              {data.convertedFileUrl && (
                <a
                  href={data.convertedFileUrl}
                  download
                  className="mt-2 inline-block px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                >
                  Download {outputFormat.toUpperCase()}
                </a>
              )}
  
              {/* Cancel Upload Button - only if not finished */}
              {data.status !== 'Conversion complete' &&
                data.status !== 'Upload canceled' &&
                data.status !== 'Assembly failed' && (
                  <button
                    onClick={() => cancelUpload(id)}
                    className="mt-2 px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    Cancel Upload
                  </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
  
export default FileUpload;
